/**
 * Поле поиска шаблона как «плейсхолдер» выпадающего поиска (SAIT-80).
 *
 * Заменяет прежние SearchHeader/SearchMenu: разметка поля и классы шаблона
 * те же (CSS шапки не трогаем), но само поле ничего не ищет — наведение
 * заранее грузит группу vpl.tas.search, фокус/клик открывает панель
 * (SearchPanelHost), у которой собственное поле ввода. Экземпляры на
 * странице: шапка и фиксированная шапка при прокрутке (variant="header"),
 * выпадающий каталог шапки (variant="mobile"), новое мобильное меню
 * (variant="menu", SAIT-110 — поле живёт внутри группы vpl.tas.mobilemenu,
 * вид варианта описан в search-trigger.scss); панель — одна.
 *
 * В варианте menu поле исключено из обхода клавишей Tab (tabindex="-1"):
 * фокус в поле открывает поиск, а открытый поиск закрывает меню, и с
 * клавиатуры было бы не дойти до пунктов меню. Касание и клик работают как
 * прежде, с клавиатуры поиск открывает кнопка «Найти».
 *
 * Надпись поля — бегущие подсказки из админки (W-103): фразы «печатаются» в
 * placeholder общим набором на все экземпляры (typed-placeholder.js), пока
 * посетитель не поставил курсор в поле. Подписи поля это лишает, поэтому у
 * поля есть aria-label.
 */
import { ref, onMounted } from 'ui.vue3';
import './search-trigger.css';
import { closeHeaderCatalog } from '../../tools/functions';
import { preloadSearchPanel, openSearch, searchState } from './search-state';
import { useTypedPlaceholder } from './typed-placeholder';

/** надпись поля, когда бегущих подсказок нет или набор стоит */
const STATIC_PLACEHOLDER = 'Поиск';

const field = `
    <form action="" @submit.prevent="open($event)">
        <div class="header__search-wrapper js-header-search">
            <input ref="inputEl" type="text" class="header__search-input js-search-input" autocomplete="off"
                   aria-label="Поиск по сайту" :placeholder="placeholder"
                   :tabindex="variant === 'menu' ? -1 : null"
                   :style="searchState.pending ? 'cursor:progress' : null"
                   v-model="value" @pointerenter="preload" @focus="open($event)" @click="open($event)"
                   @focusin="onFieldFocus" @focusout="onFieldBlur">
            <div class="header__search-close js-search-clear" @click="clear"><svg class="icon icon-close"><use xlink:href="#icon-close"></use></svg></div>
            <button class="header__search-send" type="submit" aria-label="Найти" @pointerenter="preload"><svg class="icon icon-search"><use xlink:href="#icon-search"></use></svg></button>
        </div>
    </form>
`;

export const SearchTrigger = {
    props: {
        /** header — поле в шапке (обычной и фиксированной), mobile — в выпадающем каталоге шапки, menu — в мобильном меню */
        variant: { type: String, default: 'header' },
    },
    setup() {
        const inputEl = ref(null);
        const typed = useTypedPlaceholder(STATIC_PLACEHOLDER);
        onMounted(() => typed.register(inputEl.value));

        return {
            inputEl,
            searchState,
            placeholder: typed.placeholder,
            onFieldFocus: typed.onFocus,
            onFieldBlur: typed.onBlur,
        };
    },
    data() {
        return { value: '' };
    },
    watch: {
        // панель закрыли — фраза в поле шапки не остаётся (иначе уехала бы в панель при следующем открытии)
        'searchState.open'(open) {
            if (!open) {
                this.value = '';
            }
        },
    },
    methods: {
        preload() {
            preloadSearchPanel().catch(() => { /* повторится при открытии */ });
        },
        open(event) {
            if (event && typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
            closeHeaderCatalog();
            // Фокус с плейсхолдера снимаем НЕ здесь, а в момент, когда панель
            // действительно открывается (onOpen): на медленной сети группа едет
            // ещё несколько секунд, и всё это время поле должно оставаться
            // рабочим — курсор в нём, набранное уезжает в панель (getQuery).
            openSearch(this.value, this.variant, {
                getQuery: () => this.value,
                onOpen: () => {
                    // своё поле у панели, а на мобильных клавиатура под чужим полем не нужна
                    const input = this.$el.querySelector('.js-search-input');
                    if (input && document.activeElement === input) {
                        input.blur();
                    }
                },
            });
        },
        clear() {
            this.value = '';
        },
    },
    template: `
        <div v-if="variant === 'menu'" class="header__search header__search--menu js-header-search-mob">
            <div class="container">${field}</div>
        </div>
        <div v-else-if="variant === 'mobile'" class="header__search js-header-search-mob">
            <div class="container">${field}</div>
        </div>
        <div v-else class="header__middle-center js-search-wp">
            <div class="header__search js-header-search-mob">
                <div class="container">${field}</div>
            </div>
        </div>
    `,
};
